import React from 'react';
import { ArrowRight, Star, Shield, Sparkles, CheckCircle2, Menu, X, Phone, Instagram, MapPin, Quote, Snowflake, Mic, Brain, Users, HeartHandshake, Calendar, Home, Gift } from 'lucide-react';
import { Button } from "./components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "./components/ui/carousel";
import { Card, CardContent } from "./components/ui/card";
import { Dialog, DialogContent, DialogTrigger } from "./components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./components/ui/accordion";

const WinterDecor = ({ className, size = 24 }) => (
  <Snowflake className={`absolute text-blue-200/40 pointer-events-none z-0 ${className}`} size={size} />
);

const WinterPage = () => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isPlaying, setIsPlaying] = React.useState(false);

  // Images
  const images = {
    logo: "https://customer-assets.emergentagent.com/job_fe49020f-8628-4ca3-a2d0-fa72b46b9ede/artifacts/bhe767e2_KakaoTalk_20250830_225802827.png",
    coachSitting: "https://customer-assets.emergentagent.com/job_ontheground-bjj/artifacts/dkrsy6qn_Screenshot_20250919_132816_Instagram.jpg",
    coachSelfie: "https://customer-assets.emergentagent.com/job_fe49020f-8628-4ca3-a2d0-fa72b46b9ede/artifacts/1tqhhrym_KakaoTalk_20250830_142420265_23.jpg",
    heroBg: "https://customer-assets.emergentagent.com/job_ontheground-bjj/artifacts/kc91ztu8_KakaoTalk_20250830_142422723_01.jpg",
    girlKid: "https://customer-assets.emergentagent.com/job_fe49020f-8628-4ca3-a2d0-fa72b46b9ede/artifacts/f8ttyjaq_1.jfif",
    boyKid: "https://customer-assets.emergentagent.com/job_fe49020f-8628-4ca3-a2d0-fa72b46b9ede/artifacts/pmi4fyio_6.jfif",
    gallery1: "https://customer-assets.emergentagent.com/job_ontheground-bjj/artifacts/dzflpk7i_20250623_125317.jpg",
    gallery2: "https://customer-assets.emergentagent.com/job_ontheground-bjj/artifacts/j14kf4w4_479724756_17856190326380932_4220339512121640517_n.webp.jpg",
    gallery3: "https://customer-assets.emergentagent.com/job_ontheground-bjj/artifacts/i7thvg15_479996197_17856190353380932_5565306312970967091_n.webp.jpg",
    gallery4: "https://customer-assets.emergentagent.com/job_ontheground-bjj/artifacts/pkc0bn13_Clean%20%26%20professional_20250919_120455_0000.png",
    feature1: "https://customer-assets.emergentagent.com/job_ontheground-bjj/artifacts/s5sv22xf_20250919_133738.jpg",
    feature2: "https://customer-assets.emergentagent.com/job_ontheground-bjj/artifacts/a6eljcgf_20250919133124.png"
  };

  const reviews = [
    "https://customer-assets.emergentagent.com/job_ontheground-bjj/artifacts/5ctfwwrj_Screenshot_1.jpg",
    "https://customer-assets.emergentagent.com/job_ontheground-bjj/artifacts/ygqsesir_Screenshot_2.jpg",
    "https://customer-assets.emergentagent.com/job_ontheground-bjj/artifacts/m2jvd3wx_Screenshot_3.jpg",
    "https://customer-assets.emergentagent.com/job_ontheground-bjj/artifacts/u338dp4q_Screenshot_4.jpg",
    "https://customer-assets.emergentagent.com/job_ontheground-bjj/artifacts/hggfilu1_Screenshot_5.jpg",
    "https://customer-assets.emergentagent.com/job_ontheground-bjj/artifacts/xykmws28_Screenshot_20250911_144000_Instagram.jpg"
  ];

  const handleConsultation = () => {
    window.location.href = "https://booking.naver.com/booking/10/bizes/1337119/items/6484304";
  };

  const scrollToCTA = () => {
    const ctaSection = document.getElementById('cta');
    if (ctaSection) {
      ctaSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const snowflakes = Array.from({ length: 40 }).map((_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    animationDuration: `${Math.random() * 5 + 10}s`,
    animationDelay: `-${Math.random() * 10}s`,
    opacity: Math.random() * 0.5 + 0.3,
    size: Math.random() * 10 + 10,
  }));

  return (
    <div className="min-h-screen w-full overflow-x-hidden relative font-['Pretendard']">
      {/* Falling Snow Overlay */}
      <div className="fixed inset-0 pointer-events-none z-[9999] overflow-hidden" aria-hidden="true">
        {snowflakes.map((flake) => (
          <div
            key={flake.id}
            className="snowflake text-white/80"
            style={{
              left: flake.left,
              animationDuration: flake.animationDuration,
              animationDelay: flake.animationDelay,
              opacity: flake.opacity,
              fontSize: `${flake.size}px`,
            }}
          >
            ❄
          </div>
        ))}
      </div>
      
      {/* Sticky Call Button (Mobile Only) */}
      <div className="fixed bottom-4 right-4 left-4 z-50 md:hidden">
        <a href="tel:010-9879-0818" className="flex items-center justify-center gap-2 w-full bg-[#1A4392] text-white font-bold py-4 rounded-full shadow-2xl hover:brightness-110 transition-all border border-blue-200/30">
          <Phone className="w-5 h-5 fill-white" />
          겨울방학 캠프 상담하기
        </a>
      </div>

      {/* Navigation */}
      <nav className="absolute w-full z-50 px-4 py-4">
        <div className="max-w-7xl mx-auto bg-white/90 backdrop-blur-md rounded-[25px] px-6 py-3 flex items-center shadow-md border border-blue-100 relative min-h-[64px]">
          <a href="/winter" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-2 whitespace-nowrap">
             <Snowflake className="w-6 h-6 text-[#93C5FD]" />
             <span className="text-[#1A4392] font-['Poppins'] font-extrabold text-2xl tracking-tight">온더그라운드 주짓수</span>
          </a>
          
          <div className="hidden md:flex items-center gap-8 ml-auto">
            <a href="#curriculum" className="text-[#374151] hover:text-[#1A4392] transition-colors font-medium">커리큘럼</a>
            <a href="#testimonials" className="text-[#374151] hover:text-[#1A4392] transition-colors font-medium">후기</a>
            <button onClick={scrollToCTA} className="bg-[#1A4392] text-white rounded-full px-6 py-2 font-bold hover:bg-[#153e75] transition-colors">
              상담 예약
            </button>
          </div>

          <button className="md:hidden text-[#1A4392] ml-auto" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X /> : <Menu />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="absolute top-20 left-4 right-4 bg-white/95 backdrop-blur-md rounded-2xl p-6 shadow-xl md:hidden flex flex-col gap-4 border border-blue-100 animate-in slide-in-from-top-2 z-50">
            <a href="#curriculum" className="text-[#374151] font-medium py-2" onClick={() => setIsMenuOpen(false)}>커리큘럼</a>
            <a href="#testimonials" className="text-[#374151] font-medium py-2" onClick={() => setIsMenuOpen(false)}>후기</a>
            <button onClick={() => { setIsMenuOpen(false); scrollToCTA(); }} className="bg-[#1A4392] text-white w-full py-3 rounded-xl font-bold">
              상담 예약하기
            </button>
          </div>
        )}
      </nav>

      {/* 1. Hero Section */}
      <section id="hero" className="relative pt-32 pb-16 px-4 overflow-hidden min-h-[85vh] flex flex-col items-center text-center bg-slate-50">
        <div className="fixed inset-0 z-[-1]">
            <div 
                className="absolute inset-0 bg-cover bg-[center_top_30%] bg-no-repeat opacity-60"
                style={{ backgroundImage: `url(${images.heroBg})` }}
            ></div>
            <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-blue-50/90 to-white/70"></div>
            
            {/* Stylized Large Snowflakes */}
            <div className="absolute top-20 left-10 text-blue-100/50 animate-pulse duration-[4s]">
                <Snowflake className="w-32 h-32" />
            </div>
            <div className="absolute bottom-40 right-10 text-blue-100/50 animate-pulse duration-[5s] delay-1000">
                <Snowflake className="w-48 h-48" />
            </div>
            <div className="absolute top-1/2 left-1/3 text-blue-50/80 animate-pulse duration-[6s] delay-500">
                <Snowflake className="w-24 h-24" />
            </div>
            
            {/* Snow Drifts */}
            <div className="absolute bottom-0 left-0 right-0 h-16 bg-white rounded-t-[50%] scale-x-150 translate-y-8 blur-sm opacity-80"></div>
        </div>

        <div className="max-w-4xl mx-auto relative z-10 space-y-8">
            <div className="inline-block bg-[#1A4392] text-white px-4 py-1.5 rounded-full font-bold text-sm mb-2 shadow-md tracking-wide">
              <Snowflake className="inline w-4 h-4 mr-1 mb-0.5" />
              겨울방학 6주 집중 프로그램
            </div>
            <h1 className="display-large text-4xl md:text-6xl drop-shadow-sm leading-tight text-[#0F172A]">
              자신감 넘치는 아이,<br />
              <span className="text-[#1A4392]">스스로를 지키는 힘</span><br />
              <span className="text-2xl md:text-4xl font-medium text-gray-500 mt-2 block">Winter Confidence Camp</span>
            </h1>

            {/* Video Section */}
            <div className="relative group max-w-sm mx-auto my-8">
                 <div className="absolute -inset-4 bg-[#93C5FD] rounded-[40px] -rotate-3 opacity-30 blur-xl"></div>
                 
                 {!isPlaying && (
                 <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 flex flex-col items-center animate-bounce pointer-events-none">
                    <span className="font-['Poppins'] font-bold text-[#E63946] text-lg md:text-xl bg-white px-4 py-2 rounded-full shadow-lg whitespace-nowrap mb-2 border border-red-100 transform -rotate-6">
                      클릭하세요! 🎬
                    </span>
                    <ArrowRight className="w-8 h-8 md:w-12 md:h-12 text-[#E63946] fill-[#E63946] rotate-90" />
                 </div>
                 )}

                 <video 
                    src="https://customer-assets.emergentagent.com/job_ontheground-bjj/artifacts/7zj9r6yx_1122.mp4"
                    controls
                    playsInline
                    onPlay={() => setIsPlaying(true)}
                    className="rounded-[32px] shadow-2xl w-full aspect-[4/5] object-cover bg-black border-4 border-white relative z-10"
                 >
                    Your browser does not support the video tag.
                 </video>
            </div>
            
            <div className="bg-white/60 backdrop-blur-sm p-6 rounded-2xl border border-blue-100 shadow-sm">
                <p className="text-lg md:text-xl text-[#374151] leading-relaxed font-medium word-keep-all">
                "이번 겨울, 아이가 놀이와 도전, 팀워크를 통해 진짜 자신감을 키울 수 있도록 도와주세요.
                <br/><br/>
                키즈 윈터 컨피던스 캠프는 아이들의 자신감, 스스로 해결하는 힘, 체력을 자연스럽게 향상시키며 잊지 못할 겨울 방학을 선물합니다."
                </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 pt-4 justify-center">
              <button onClick={scrollToCTA} className="btn-cta bg-[#1A4392] text-white hover:bg-[#153e75] shadow-lg shadow-blue-200/50 w-full sm:w-auto border-0">
                캠프 등록 상담하기
              </button>
              <button onClick={scrollToCTA} className="bg-white text-[#1A4392] border-2 border-[#1A4392] px-8 py-3.5 rounded-full font-bold text-xl hover:bg-blue-50 transition-all w-full sm:w-auto">
                1일 체험 신청
              </button>
            </div>
        </div>
      </section>

      {/* 2. Why Confidence Matters */}
      <section id="benefits" className="py-10 px-4 bg-white relative overflow-hidden">
        <WinterDecor className="top-10 left-10 w-32 h-32 rotate-12" size={128} />
        <WinterDecor className="bottom-10 right-10 w-40 h-40 -rotate-12" size={160} />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center p-2 bg-blue-50 rounded-full mb-4">
                <Snowflake className="w-6 h-6 text-[#1A4392] mr-2" />
                <span className="text-[#1A4392] font-bold">Winter Confidence</span>
                <Snowflake className="w-6 h-6 text-[#1A4392] ml-2" />
            </div>
            <h2 className="text-3xl md:text-5xl font-bold mb-6 text-[#1A4392]">왜 '자신감'이 중요할까요?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto word-keep-all">
              두려움은 과잉 반응과 싸움을 부르지만, <br className="md:hidden"/>진정한 자신감은 차분함과 평화를 만듭니다.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-blue-50/80 backdrop-blur-sm rounded-[32px] p-8 hover:-translate-y-2 transition-transform duration-300 border border-blue-100 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-20">
                  <Snowflake className="w-12 h-12 text-[#1A4392]" />
              </div>
              <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mb-6 shadow-sm">
                <Mic className="w-7 h-7 text-[#1A4392]" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-[#1A4392]">자신감과 갈등 해결</h3>
              <p className="text-gray-600 leading-relaxed">
                자신감이 있으면 갈등 상황에서도 당황하지 않습니다. 상황을 차분하게 인지하고(De-escalation), 대화를 통해 문제를 평화롭게 해결할 수 있는 능력을 기릅니다.
              </p>
            </div>

            <div className="bg-blue-50/80 backdrop-blur-sm rounded-[32px] p-8 hover:-translate-y-2 transition-transform duration-300 border border-blue-100 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-20">
                  <Snowflake className="w-12 h-12 text-[#1A4392]" />
              </div>
              <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mb-6 shadow-sm">
                <Shield className="w-7 h-7 text-[#1A4392]" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-[#1A4392]">비폭력 호신술</h3>
              <p className="text-gray-600 leading-relaxed">
                주먹을 쓰지 않고도 상대를 제압하고 상황을 안전하게 종료시키는 주짓수 기반의 셀프 디펜스를 익힙니다.
              </p>
            </div>

            <div className="bg-blue-50/80 backdrop-blur-sm rounded-[32px] p-8 hover:-translate-y-2 transition-transform duration-300 border border-blue-100 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-20">
                  <Snowflake className="w-12 h-12 text-[#1A4392]" />
              </div>
              <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mb-6 shadow-sm">
                <HeartHandshake className="w-7 h-7 text-[#1A4392]" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-[#1A4392]">회복탄력성</h3>
              <p className="text-gray-600 leading-relaxed">
                안전한 환경에서 어려운 상황을 극복하고 긍정적인 결과를 반복적으로 경험하며, 실패를 두려워하지 않는 단단한 마음을 기릅니다.
              </p>
            </div>

            <div className="bg-blue-50/80 backdrop-blur-sm rounded-[32px] p-8 hover:-translate-y-2 transition-transform duration-300 border border-blue-100 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-20">
                  <Snowflake className="w-12 h-12 text-[#1A4392]" />
              </div>
              <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mb-6 shadow-sm">
                <Brain className="w-7 h-7 text-[#1A4392]" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-[#1A4392]">감정 조절 능력</h3>
              <p className="text-gray-600 leading-relaxed">
                압박감 속에서도 흥분하지 않고 침착함을 유지하는 'Emotional Regulation' 훈련을 통해 내면의 힘을 기릅니다.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Camp Features Overview (New Grouped Section) */}
      <section id="features" className="py-10 px-4 bg-blue-50/30">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6">
             {/* Feature 1 */}
             <div className="bg-white p-6 rounded-2xl shadow-sm text-center flex flex-col items-center justify-center hover:shadow-md transition-shadow">
                <Shield className="w-8 h-8 text-[#1A4392] mb-3" />
                <span className="font-bold text-[#374151] word-keep-all">학교폭력 예방 교육</span>
             </div>
             {/* Feature 2 */}
             <div className="bg-white p-6 rounded-2xl shadow-sm text-center flex flex-col items-center justify-center hover:shadow-md transition-shadow">
                <HeartHandshake className="w-8 h-8 text-[#1A4392] mb-3" />
                <span className="font-bold text-[#374151] word-keep-all">회복탄력성 강화</span>
             </div>
             {/* Feature 3 */}
             <div className="bg-white p-6 rounded-2xl shadow-sm text-center flex flex-col items-center justify-center hover:shadow-md transition-shadow">
                <Users className="w-8 h-8 text-[#E63946] mb-3" />
                <span className="font-bold text-[#374151] word-keep-all">초등 4학년 이상</span>
             </div>
             {/* Feature 4 */}
             <div className="bg-white p-6 rounded-2xl shadow-sm text-center flex flex-col items-center justify-center hover:shadow-md transition-shadow">
                <Star className="w-8 h-8 text-[#D3FF62] fill-[#D3FF62] mb-3" />
                <span className="font-bold text-[#374151] word-keep-all">초보자 가능</span>
             </div>
             {/* Feature 5 */}
             <div className="bg-white p-6 rounded-2xl shadow-sm text-center flex flex-col items-center justify-center hover:shadow-md transition-shadow col-span-2 md:col-span-1">
                <Sparkles className="w-8 h-8 text-[#1A4392] mb-3" />
                <span className="font-bold text-[#374151] word-keep-all">영어 몰입형 환경</span>
             </div>
          </div>
        </div>
      </section>

      {/* 4. Coach Section */}
      <section id="coach" className="py-10 px-4 bg-white border-t border-gray-100">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row gap-12 items-center">
          <div className="w-full md:w-1/3">
             <img src={images.coachSitting} alt="Thomas Coach" className="rounded-[32px] shadow-xl w-full hover:scale-105 transition-transform duration-500" />
          </div>
          <div className="w-full md:w-2/3 space-y-6">
            <h2 className="text-3xl font-bold text-[#1A4392]">토마스 관장 <span className="text-lg font-normal text-[#6B7280] block md:inline mt-1 md:mt-0 font-sans">원어민 블랙벨트 지도자</span></h2>
            
            <div className="space-y-2 md:space-y-3">
              <div className="flex items-center gap-3 text-[#374151] font-medium text-base md:text-base">
                <CheckCircle2 className="w-5 h-5 text-[#1A4392]" /> 17년+ 수성구 영어 교육 경력
              </div>
              <div className="flex items-center gap-3 text-[#374151] font-medium text-base md:text-base">
                <CheckCircle2 className="w-5 h-5 text-[#1A4392]" /> 11년 주짓수 수련, 블랙벨트
              </div>
              <div className="flex items-center gap-3 text-[#374151] font-medium text-base md:text-base">
                <CheckCircle2 className="w-5 h-5 text-[#1A4392]" /> 어린이 교육 전문 과정 수료
              </div>
              <div className="flex items-center gap-3 text-[#374151] font-medium text-base md:text-base">
                <CheckCircle2 className="w-5 h-5 text-[#1A4392]" /> 철학과 언어학 전공
              </div>
            </div>

            <blockquote className="bg-blue-50 p-4 md:p-6 rounded-2xl italic text-lg md:text-lg text-[#1A4392] border-l-4 border-[#1A4392]">
              "주짓수를 통해 학생들에게 끈기, 자신감, 그리고 자기주도성과 같은 삶의 기술을 가르치고 싶습니다."
            </blockquote>
            
            <div className="pt-4">
                <button onClick={scrollToCTA} className="btn-primary w-full md:w-auto text-lg">
                    한국인 원장님과 상담하기
                </button>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Limited Offer Section */}
      <section id="offer" className="py-10 px-4 bg-white/60 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto bg-white/90 backdrop-blur-xl rounded-[32px] md:rounded-[40px] shadow-2xl overflow-hidden border border-blue-200 relative">
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50 rounded-full blur-3xl -z-10 -translate-y-1/2 translate-x-1/2"></div>
          <div className="bg-[#1A4392] text-white p-8 md:p-12 text-center overflow-hidden">
            <span className="bg-[#E63946] text-white px-4 py-1.5 rounded-full font-bold text-xs md:text-sm uppercase tracking-wider mb-6 inline-block shadow-lg">Limited Offer</span>
            <h2 className="text-xl md:text-2xl font-medium mb-8 font-['Poppins'] text-blue-100">100% 환불 보장 + 무료 체험</h2>
            
            <div className="relative inline-block">
                {/* Decorative Arrows (Desktop) */}
                <div className="absolute top-1/2 -left-16 -translate-y-1/2 hidden md:block animate-bounce">
                    <ArrowRight className="w-12 h-12 text-[#D3FF62]" />
                </div>
                <div className="absolute top-1/2 -right-16 -translate-y-1/2 hidden md:block animate-bounce">
                    <ArrowRight className="w-12 h-12 text-[#D3FF62] rotate-180" />
                </div>

                {/* Mobile Arrow (Top) */}
                <div className="absolute -top-8 left-1/2 -translate-x-1/2 md:hidden animate-bounce">
                    <ArrowRight className="w-8 h-8 text-[#D3FF62] rotate-90" />
                </div>

                <p className="text-white text-4xl md:text-6xl font-black leading-tight drop-shadow-2xl">
                    리스크 <span className="text-[#D3FF62]">0%</span>,<br/>
                    성장 가능성 <span className="text-[#D3FF62]">100%</span>
                </p>
            </div>
          </div>
          
          <div className="p-6 md:p-12">
             <div className="space-y-6 md:space-y-8 mb-8 md:mb-10">
               <div className="flex items-start gap-3 md:gap-4 group">
                 <div className="bg-blue-50 p-3 md:p-4 rounded-2xl group-hover:bg-blue-100 transition-colors shrink-0">
                   <span className="text-2xl md:text-2xl">🎁</span>
                 </div>
                 <div>
                   <h3 className="text-xl md:text-xl font-bold text-[#1A4392]">무료 체험 수업 1회</h3>
                   <p className="text-base md:text-base text-[#6B7280]">30,000원 상당 - 아이가 직접 경험 후 결정합니다</p>
                 </div>
               </div>
               
               <div className="flex items-start gap-3 md:gap-4 group">
                 <div className="bg-blue-50 p-3 md:p-4 rounded-2xl group-hover:bg-blue-100 transition-colors shrink-0">
                   <span className="text-2xl md:text-2xl">🥋</span>
                 </div>
                 <div>
                   <h3 className="text-xl md:text-xl font-bold text-[#1A4392]">무료 도복 증정</h3>
                   <p className="text-base md:text-base text-[#6B7280]">100,000원 상당 - 첫날부터 자신감 있게 시작</p>
                 </div>
               </div>

               <div className="flex items-start gap-3 md:gap-4 group">
                 <div className="bg-blue-50 p-3 md:p-4 rounded-2xl group-hover:bg-blue-100 transition-colors shrink-0">
                   <span className="text-2xl md:text-2xl">🎒</span>
                 </div>
                 <div>
                   <h3 className="text-xl md:text-xl font-bold text-[#1A4392]">무료 도복 가방 증정</h3>
                   <p className="text-base md:text-base text-[#6B7280]">5,000원 상당 - 튼튼하고 실용적인 전용 가방</p>
                 </div>
               </div>
             </div>

             <div className="text-center p-4 md:p-6 bg-blue-50 rounded-2xl border border-blue-100 mb-6 md:mb-8">
               <p className="font-bold text-[#1A4392] text-lg md:text-lg mb-1">총 혜택: 135,000원 상당</p>
               <p className="text-sm md:text-sm text-[#6B7280]">만족하지 않으면 전액 환불해드립니다</p>
             </div>

             <div className="flex flex-col gap-3">
                <button onClick={scrollToCTA} className="w-full btn-cta text-center justify-center shadow-xl shadow-red-100 text-xl md:text-xl py-4 md:py-5">
                혜택 받고 시작하기
                </button>
                <p className="text-center text-xs md:text-xs text-gray-400 mt-1 md:mt-2">선착순 마감될 수 있습니다.</p>
             </div>
          </div>
        </div>
      </section>

      {/* 6. 6-Week Curriculum */}
      <section id="curriculum" className="py-10 px-4 bg-[#F8FAFC] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/snow.png')] opacity-40 pointer-events-none"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-12">
             <h2 className="text-3xl md:text-5xl font-bold mb-6 text-[#1A4392] leading-tight">
               6주 윈터 컨피던스 캠프 커리큘럼
             </h2>
             <p className="text-lg text-gray-600 mb-8 leading-relaxed">
               모든 기술은 초보자도 쉽게 따라할 수 있는 기초 레벨로 진행됩니다.
             </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Week 1 */}
             <div className="bg-white p-6 rounded-2xl shadow-md border-l-4 border-[#1A4392] relative overflow-hidden">
                <WinterDecor className="top-2 right-2 w-8 h-8 opacity-20" size={32} />
                <div className="flex justify-between items-center mb-2 relative z-10">
                   <h4 className="font-bold text-xl text-[#1A4392]">WEEK 1</h4>
                   <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-bold">기초</span>
                </div>
                <h5 className="font-bold text-lg text-gray-900 mb-2 relative z-10">자신감, 자세, 그리고 안전거리</h5>
                <p className="text-gray-600 text-sm relative z-10">당당하게 서기, 호흡법, 안전거리 유지하기 (두려움 극복 및 상황 인지)</p>
             </div>

             {/* Week 2 */}
             <div className="bg-white p-6 rounded-2xl shadow-md border-l-4 border-[#1A4392] relative overflow-hidden">
                <WinterDecor className="top-2 right-2 w-8 h-8 opacity-20" size={32} />
                <div className="flex justify-between items-center mb-2 relative z-10">
                   <h4 className="font-bold text-xl text-[#1A4392]">WEEK 2</h4>
                   <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-bold">스탠딩</span>
                </div>
                <h5 className="font-bold text-lg text-gray-900 mb-2 relative z-10">스탠딩 자기방어 기초</h5>
                <p className="text-gray-600 text-sm relative z-10">균형 잡기, 밀치기 방어, 넘어짐 방지 (침착하고 통제된 움직임)</p>
             </div>

             {/* Week 3 */}
             <div className="bg-white p-6 rounded-2xl shadow-md border-l-4 border-[#E63946] relative overflow-hidden">
                <WinterDecor className="top-2 right-2 w-8 h-8 opacity-20 text-red-200" size={32} />
                <div className="flex justify-between items-center mb-2 relative z-10">
                   <h4 className="font-bold text-xl text-[#E63946]">WEEK 3</h4>
                   <span className="text-sm bg-red-100 text-red-800 px-2 py-1 rounded-full font-bold">탈출</span>
                </div>
                <h5 className="font-bold text-lg text-gray-900 mb-2 relative z-10">손목 및 옷 잡혔을 때 탈출하기</h5>
                <p className="text-gray-600 text-sm relative z-10">손목 및 옷 잡혔을 때 탈출하기 + 안전하게 거리두기 (상황 악화 없이 부드럽게 탈출)</p>
             </div>

             {/* Week 4 */}
             <div className="bg-white p-6 rounded-2xl shadow-md border-l-4 border-[#E63946] relative overflow-hidden">
                <WinterDecor className="top-2 right-2 w-8 h-8 opacity-20 text-red-200" size={32} />
                <div className="flex justify-between items-center mb-2 relative z-10">
                   <h4 className="font-bold text-xl text-[#E63946]">WEEK 4</h4>
                   <span className="text-sm bg-red-100 text-red-800 px-2 py-1 rounded-full font-bold">그라운드</span>
                </div>
                <h5 className="font-bold text-lg text-gray-900 mb-2 relative z-10">그라운드 안전 및 안전하게 일어나기</h5>
                <p className="text-gray-600 text-sm relative z-10">기본 그라운드 방어 자세 및 안전하게 일어나는 법 (넘어져도 당황하지 않고 보호하기)</p>
             </div>

             {/* Week 5 */}
             <div className="bg-white p-6 rounded-2xl shadow-md border-l-4 border-[#1A4392] relative overflow-hidden">
                <WinterDecor className="top-2 right-2 w-8 h-8 opacity-20" size={32} />
                <div className="flex justify-between items-center mb-2 relative z-10">
                   <h4 className="font-bold text-xl text-[#1A4392]">WEEK 5</h4>
                   <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-bold">방어</span>
                </div>
                <h5 className="font-bold text-lg text-gray-900 mb-2 relative z-10">펀치 방어 시리즈 기초</h5>
                <p className="text-gray-600 text-sm relative z-10">거리 조절, 머리 보호, 타격 없이 상대 제압하기 (높은 압박 상황에서도 침착함 유지)</p>
             </div>

             {/* Week 6 */}
             <div className="bg-white p-6 rounded-2xl shadow-md border-l-4 border-[#1A4392] relative overflow-hidden">
                <WinterDecor className="top-2 right-2 w-8 h-8 opacity-20" size={32} />
                <div className="flex justify-between items-center mb-2 relative z-10">
                   <h4 className="font-bold text-xl text-[#1A4392]">WEEK 6</h4>
                   <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-bold">종합</span>
                </div>
                <h5 className="font-bold text-lg text-gray-900 mb-2 relative z-10">실전 시나리오 연습 및 복습</h5>
                <p className="text-gray-600 text-sm relative z-10">배운 기술을 활용한 가벼운 상황별 시뮬레이션 (일상 생활 적용 자신감 획득)</p>
             </div>
          </div>
        </div>
      </section>

      {/* 7. Testimonials Section */}
      <section id="testimonials" className="py-10 px-4 bg-[#1A4392]/90 backdrop-blur-sm text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-5xl font-bold mb-3 md:mb-6 text-white font-['Poppins']">
              학부모님들의 리얼 후기
            </h2>
            <p className="text-blue-200 text-lg md:text-xl">이미지를 클릭하면 크게 볼 수 있습니다</p>
          </div>

          <div className="max-w-5xl mx-auto">
             <Carousel
                opts={{
                    align: "start",
                    loop: true,
                }}
                className="w-full"
             >
                <CarouselContent className="-ml-4">
                    {reviews.map((src, index) => (
                        <CarouselItem key={index} className="pl-4 md:basis-1/2 lg:basis-1/3 h-[400px] md:h-[500px]">
                            <div className="p-1 h-full">
                              <Dialog>
                                <DialogTrigger asChild>
                                  <Card className="border-0 overflow-hidden rounded-[20px] cursor-zoom-in group h-full shadow-lg hover:shadow-2xl transition-shadow">
                                      <CardContent className="p-0 h-full relative bg-gray-100">
                                          <img 
                                              src={src} 
                                              alt={`Review ${index + 1}`} 
                                              loading="lazy"
                                              className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                                          />
                                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                                              <span className="bg-white/90 text-blue-900 text-sm font-bold px-4 py-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity translate-y-4 group-hover:translate-y-0 duration-300 shadow-md">
                                                크게 보기
                                              </span>
                                          </div>
                                      </CardContent>
                                  </Card>
                                </DialogTrigger>
                                <DialogContent className="max-w-[95vw] max-h-[95vh] w-auto h-auto p-0 bg-transparent border-none shadow-none flex justify-center items-center outline-none [&>button]:text-white [&>button]:bg-black/20 [&>button]:rounded-full [&>button]:p-1">
                                   <div className="relative w-auto h-auto max-w-full max-h-full overflow-hidden rounded-lg bg-white">
                                     <img
                                       src={src}
                                       alt="Review Fullscreen"
                                       className="w-auto h-auto max-w-[90vw] max-h-[90vh] object-contain"
                                     />
                                   </div>
                                </DialogContent>
                              </Dialog>
                            </div>
                        </CarouselItem>
                    ))}
                </CarouselContent>
                <CarouselPrevious className="flex -left-6 md:-left-24 bg-white hover:bg-red-50 text-[#E63946] border-4 border-[#E63946] shadow-2xl z-10 h-16 w-16 md:h-24 md:w-24 [&>svg]:h-8 [&>svg]:w-8 md:[&>svg]:h-12 md:[&>svg]:w-12 rounded-full transition-transform hover:scale-110" />
                <CarouselNext className="flex -right-6 md:-right-24 bg-white hover:bg-red-50 text-[#E63946] border-4 border-[#E63946] shadow-2xl z-10 h-16 w-16 md:h-24 md:w-24 [&>svg]:h-8 [&>svg]:w-8 md:[&>svg]:h-12 md:[&>svg]:w-12 rounded-full transition-transform hover:scale-110" />
             </Carousel>
          </div>
          
          <div className="text-center mt-8 md:mt-12">
            <a 
                href="https://map.naver.com/p/entry/place/1096514067"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex btn-cta bg-white text-[#1A4392] hover:bg-gray-100 shadow-none text-base md:text-xl px-6 py-3 md:px-10 md:py-5 min-h-[48px] md:min-h-[60px] items-center justify-center font-bold rounded-full"
            >
                더 많은 후기 보러가기
            </a>
          </div>
        </div>
      </section>

      {/* 8. FAQ Section */}
      <section id="faq" className="py-10 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-center text-3xl font-bold mb-10 text-[#1A4392]">자주 묻는 질문 (FAQ)</h2>
          
          <Accordion type="single" collapsible className="w-full space-y-4">
            {[
              {
                q: "캠프 기간은 언제인가요?",
                a: "1월 초부터 2월 중순까지 6주간 진행됩니다. 주 3회 또는 주 5회 선택 가능합니다."
              },
              {
                q: "영어를 전혀 못해도 괜찮나요?",
                a: "네, 괜찮습니다. 영어 실력과 상관없이 누구나 자연스럽게 따라오도록 수업이 진행됩니다. 한국어 설명도 함께 진행됩니다."
              },
              {
                q: "운동을 처음 하는 아이도 괜찮나요?",
                a: "네, 물론입니다. 이번 캠프는 '자신감'과 '기초 체력'에 초점을 맞추어 초보자도 재미있게 따라할 수 있는 프로그램으로 구성되었습니다."
              },
              {
                q: "다치지는 않나요?",
                a: "철저한 안전 규칙과 단계별 지도 방식을 기반으로 수업이 진행됩니다. 통계적으로도 축구, 배구, 복싱 등 흔히 접하는 스포츠보다 부상률이 낮아 안전합니다."
              },
              {
                q: "여자 아이들도 많이 참여하나요?",
                a: "네, 여학생 비율이 높습니다. 자기방어 기술은 성별에 관계없이 꼭 필요한 능력이며, 안전하고 서로 존중하는 분위기에서 수업합니다."
              },
              {
                q: "도복이 꼭 필요한가요?",
                a: "네, 필요합니다. 하지만 이번 캠프 등록 시 10만원 상당의 고급 도복을 무료로 제공해드립니다."
              },
              {
                q: "체험 수업이 가능한가요?",
                a: "네. 무료 체험 수업을 제공하고 있으며, 아이가 분위기와 운동을 직접 경험해볼 수 있습니다."
              },
              {
                q: "몇 살부터 수업을 들을 수 있나요?",
                a: "초등학교 4학년부터 중·고등학생까지 참여할 수 있으며, 각 연령대가 자연스럽게 따라올 수 있도록 수업을 구성합니다."
              },
              {
                q: "반 정원은 어떻게 되나요?",
                a: "수업은 최대 12명 정원으로 운영되며, 모든 아이가 충분한 지도와 안전한 환경 속에서 수업을 받을 수 있도록 합니다."
              },
              {
                q: "차량 운행을 하나요? / 주차가 가능한가요?",
                a: "현재 차량 운행은 하지 않습니다. 만촌역 3번 출구에서 232m 거리로 대중교통 이용이 편리합니다. 주차는 네이버 플레이스 또는 전화로 확인해주세요."
              }
            ].map((item, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border rounded-2xl px-4 bg-slate-50 shadow-sm">
                <AccordionTrigger className="text-left font-bold text-[#1A4392] py-4 hover:no-underline text-lg md:text-xl">
                  Q. {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-4 leading-relaxed text-base md:text-lg">
                  A. {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* 9. Final CTA Section */}
      <section id="cta" className="py-12 px-4 bg-[#1A4392] text-white text-center">
        <div className="max-w-3xl mx-auto space-y-6">
          <h2 className="text-3xl font-bold font-['Poppins']">Ready to Start?</h2>
          <p className="text-xl text-blue-100">지금 바로 상담을 예약하고 혜택을 놓치지 마세요.</p>
          <button onClick={handleConsultation} className="btn-cta bg-[#D3FF62] text-[#1A4392] hover:bg-[#b8e640] px-12 py-4 rounded-full text-xl font-bold shadow-lg hover:scale-105 transition-transform">
            무료 상담 예약하기
          </button>
          
          <div className="flex justify-center gap-6 pt-6">
            <div className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-[#93C5FD]" /> 
              <span className="text-lg">010-9879-0818</span>
            </div>
            <div className="flex items-center gap-2">
              <Instagram className="w-5 h-5 text-[#93C5FD]" /> 
              <span className="text-lg">@onthegroundbjj</span>
            </div>
          </div>
        </div>
      </section>

      {/* 10. Footer */}
      <footer id="footer" className="bg-[#0F172A] text-white pt-12 pb-8 md:pt-20 md:pb-10 px-4">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12 mb-8 md:mb-16">
          <div>
            <h3 className="text-2xl font-bold mb-6 font-['Poppins']">ONTHEGROUND</h3>
            <p className="opacity-70 mb-6 leading-relaxed">영어와 운동을 함께 배우는<br/>대구 유일의 프리미엄 키즈 주짓수</p>
            <div className="flex gap-4">
              <a href="https://instagram.com/onthegroundbjj" target="_blank" rel="noopener noreferrer" className="bg-white/10 p-3 rounded-full hover:bg-[#E1306C] transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>
          
          <div>
            <h4 className="text-lg font-bold mb-6 text-[#E63946] font-['Poppins']">Contact</h4>
            <ul className="space-y-4 opacity-80">
              <li className="flex items-center gap-3"><Phone className="w-4 h-4" /> 010-9879-0818</li>
              <li className="flex items-center gap-3"><Instagram className="w-4 h-4" /> @onthegroundbjj</li>
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-bold mb-6 text-[#E63946] font-['Poppins']">Location</h4>
            <p className="opacity-80 flex gap-2 leading-relaxed mb-4">
              <MapPin className="w-5 h-5 shrink-0" />
              대구 수성구 달구벌대로 2610 2층<br/>만촌역 3번 출구에서 232m
            </p>
            <a 
              href="https://map.naver.com/p/entry/place/1096514067" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block w-28 rounded-2xl overflow-hidden bg-white shadow-lg border border-white/20 hover:scale-[1.02] transition-transform duration-300 group relative"
            >
               <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors z-10 flex items-center justify-center">
                  <span className="bg-[#03C75A] text-white text-[10px] font-bold px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-all transform translate-y-2 group-hover:translate-y-0 shadow-md whitespace-nowrap">
                    지도 보기
                  </span>
               </div>
               <img 
                 src="https://customer-assets.emergentagent.com/job_ontheground-bjj/artifacts/na9l4hfc_image.png"
                 alt="Naver Map"
                 className="w-full h-auto object-cover"
               />
            </a>
          </div>

          <div>
             <button onClick={handleConsultation} className="w-full bg-white text-[#0F172A] py-4 rounded-xl font-bold hover:bg-blue-50 transition-colors shadow-lg">
               무료 상담 예약하기
             </button>
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto border-t border-white/10 pt-8 text-center text-sm opacity-50 font-light">
          <p>&copy; 2025 ONTHEGROUND BJJ. All rights reserved.</p>
          <div className="mt-4 flex justify-center gap-3">
            <a 
              href="/affiliates" 
              className="inline-flex items-center gap-2 text-white/60 hover:text-[#E63946] transition-colors text-xs font-medium px-3 py-1.5 rounded-full border border-white/10 hover:border-[#E63946]/30 hover:bg-white/5"
            >
              <Gift className="w-3 h-3" />
              추천인 리워드 안내
            </a>
            <a 
              href="/" 
              className="inline-flex items-center gap-2 text-white/60 hover:text-[#93C5FD] transition-colors text-xs font-medium px-3 py-1.5 rounded-full border border-white/10 hover:border-[#93C5FD]/30 hover:bg-white/5"
            >
              <Home className="w-3 h-3" />
              메인 홈페이지로 이동
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default WinterPage;
